
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPaginatedCustomers, searchCustomers } from '@/services/customerService';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import CreateCustomerDialog from '@/components/customers/CreateCustomerDialog';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from '@/hooks/useAuth';
import TablePagination from '@/components/ui/TablePagination';
import { useLanguage } from '@/hooks/useLanguage';
import { useDebounce } from '@/hooks/useDebounce';

const ITEMS_PER_PAGE = 10;

const Customers = () => {
  const { translate } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { user, isLoading: authLoading } = useAuth();
  const [isSearching, setIsSearching] = useState(false);
  const [sortField, setSortField] = useState('customer_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Debounce search input to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Updated query to use backend pagination
  const { data, refetch, isLoading } = useQuery({
    queryKey: ['customers', currentPage, ITEMS_PER_PAGE, debouncedSearchTerm, sortField, sortDirection],
    queryFn: async () => {
      if (debouncedSearchTerm.length >= 3) {
        setIsSearching(true);
        try {
          // For searches, we'll still use the existing search function 
          // but in a real app, we would integrate this with pagination
          const results = await searchCustomers(debouncedSearchTerm);
          setIsSearching(false);
          console.log(`Search results for "${debouncedSearchTerm}":`, results.length);
          
          // Return in the same format as paginated results for consistency
          return { 
            data: results,
            totalCount: results.length,
            page: 1,
            pageSize: results.length
          };
        } catch (error) {
          setIsSearching(false);
          console.error('Error searching customers:', error);
          throw error;
        }
      } else {
        // Use the new paginated function
        try {
          const { data: customers, count } = await getPaginatedCustomers(
            currentPage,
            ITEMS_PER_PAGE,
            '',
            sortField,
            sortDirection
          );
          
          return {
            data: customers,
            totalCount: count,
            page: currentPage,
            pageSize: ITEMS_PER_PAGE
          };
        } catch (error) {
          console.error('Error fetching paginated customers:', error);
          throw error;
        }
      }
    },
    staleTime: 300000, // 5 minutes
    retry: (failureCount) => failureCount < 2 // Only retry twice max
  });

  const customers = data?.data || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  
  // Calculate start and end of shown records for display
  const startRecord = totalCount > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const endRecord = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const handleCustomerCreated = () => {
    refetch();
  };

  return (
    <MainLayout>
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{translate('customers.title')}</h1>
          <p className="text-muted-foreground">{translate('customers.subtitle')}</p>
        </div>
        {user && <CreateCustomerDialog onCustomerCreated={handleCustomerCreated} />}
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle>{translate('customers.search')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder={translate('customers.searchPlaceholder')} 
              className="pl-8" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isSearching && (
              <div className="absolute right-2 top-2.5">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          {debouncedSearchTerm.length > 0 && debouncedSearchTerm.length < 3 && (
            <p className="text-xs text-muted-foreground mt-2">
              {translate('common.typeAtLeast3Chars')}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{translate('customers.customerNumber')}</TableHead>
              <TableHead>{translate('customers.customerName')}</TableHead>
              <TableHead>{translate('customers.email')}</TableHead>
              <TableHead>{translate('customers.collectiveInvoice')}</TableHead>
              <TableHead>{translate('customers.created')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading || authLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  {translate('customers.loading')}
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  {searchTerm ? translate('customers.noMatchingCustomers') : translate('customers.noCustomersFound')}
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>{customer.customer_number || '-'}</TableCell>
                  <TableCell className="font-medium">{customer.customer_name}</TableCell>
                  <TableCell>{customer.email || '-'}</TableCell>
                  <TableCell>{customer.collective_invoice ? translate('common.yes') : translate('common.no')}</TableCell>
                  <TableCell>
                    {format(new Date(customer.created_at), 'MMM d, yyyy')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination and record count with total from server */}
      {totalCount > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-muted-foreground">
              {translate('subscriptions.showing')} {startRecord} - {endRecord} {translate('subscriptions.of')} {totalCount} {translate(totalCount === 1 ? 'customers.record' : 'customers.records')}
            </div>
          </div>
          
          <div className="flex justify-center">
            <TablePagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Customers;
